import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Bot, ChevronLeft, Send, Flame, Wrench, Zap } from 'lucide-react';
import { IMAGES, bgStyle } from '@/config/images';

const LaserAskAI = () => {
  const [params] = useSearchParams();
  const machineId = params.get('machine_id') || '';
  const token = localStorage.getItem('token');

  const [machines, setMachines] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    const load = async () => {
      if (!token) return;
      try {
        const res = await axios.get(`${API}/laser/machines`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setMachines(res.data || []);
      } catch (e) {
        console.error('Failed to load laser machines for troubleshooting chat', e);
      }
    };
    load();
  }, [token]);

  const machine = useMemo(
    () => (machineId ? machines.find((m) => m.id === machineId) || null : null),
    [machines, machineId]
  );

  const intro = useMemo(() => {
    if (machine) {
      return `Troubleshooting context loaded for your laser: ${machine.name || 'Laser'}${machine.brand ? ` (${machine.brand} ${machine.model || ''})` : ''} - ${machine.laser_type?.toUpperCase()} ${machine.power_watts}W. Describe your issue - what symptoms are you seeing?`;
    }
    return 'Select a laser machine from the Troubleshooting page to get personalized help, or describe your laser cutting/engraving issue below.';
  }, [machine]);

  useEffect(() => {
    setMessages([
      {
        role: 'assistant',
        content: intro,
      },
    ]);
    setSessionId(null);
  }, [intro]);

  const send = async (e) => {
    e.preventDefault();
    if (!input.trim() || !token) return;

    const text = input;
    setInput('');
    setMessages((prev) => [...prev, { role: 'user', content: text }]);
    setIsLoading(true);

    try {
      const payload = {
        message: text,
        session_id: sessionId,
        machine_id: machineId || undefined,
      };

      const res = await axios.post(`${API}/laser/ai/chat`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages((prev) => [...prev, { role: 'assistant', content: res.data.response }]);
      setSessionId(res.data.session_id);
    } catch (err) {
      toast.error('Failed to get AI response');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div 
      className="min-h-screen -m-4 lg:-m-6 p-4 lg:p-6"
      style={bgStyle(IMAGES.AI_ASSISTANT)}
      data-testid="laser-ask-ai"
    >
      <div className="fixed inset-0 bg-black/80 -z-10" style={{ pointerEvents: 'none' }} />

      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <Link to="/laser/troubleshooting" className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors">
            <ChevronLeft className="w-5 h-5" />
            Back
          </Link>
        </div>

        <div className="mb-6">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-lg bg-orange-500/20">
              <Bot className="w-8 h-8 text-orange-500" />
            </div>
            <div>
              <div className="text-xs font-bold uppercase tracking-wider text-orange-500 mb-1">AI ASSISTANT</div>
              <h1 
                className="text-3xl md:text-4xl font-bold text-orange-500 tracking-tight uppercase"
                style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
              >
                Ask AI - Laser Expert
              </h1>
              <p className="text-sm md:text-base text-zinc-300 mt-1">
                {machine 
                  ? `Getting help for: ${machine.name}` 
                  : 'Get AI-powered laser troubleshooting help'
                }
              </p>
            </div>
          </div>
          
          {machine && (
            <div className="mt-4 p-3 bg-black/70 backdrop-blur-sm rounded-lg border border-orange-500/30 flex items-center gap-3">
              <Flame className="w-5 h-5 text-orange-400" />
              <div>
                <div className="text-white font-medium">{machine.name}</div>
                <div className="text-xs text-zinc-400">
                  {machine.brand} {machine.model} • {machine.laser_type?.toUpperCase()} {machine.power_watts}W • {machine.bed_width}x{machine.bed_height}mm bed
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Chat Panel */}
        <div className="bg-black/70 backdrop-blur-sm border border-zinc-800 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3 text-sm text-zinc-400">
            <Wrench className="w-4 h-4 text-red-400" />
            <span>
              Tip: Include your material type, thickness, current power/speed settings, and describe what the cut or engrave looks like.
            </span>
          </div>

          <ScrollArea className="h-[460px] w-full rounded-lg border border-zinc-700 bg-[#1f1f1f]/80 p-4">
            <div className="space-y-3">
              {messages.map((m, idx) => (
                <div
                  key={idx}
                  className={
                    m.role === 'user'
                      ? 'flex justify-end'
                      : 'flex justify-start'
                  }
                >
                  <div
                    className={
                      m.role === 'user'
                        ? 'max-w-[85%] rounded-lg bg-red-500/15 border border-red-500/30 px-3 py-2 text-sm text-white'
                        : 'max-w-[85%] rounded-lg bg-zinc-800 border border-zinc-700 px-3 py-2 text-sm'
                    }
                  >
                    {m.role === 'assistant' && (
                      <div className="flex items-center gap-2 mb-1 text-xs text-red-500">
                        <Bot className="w-3.5 h-3.5" />
                        AI Assistant
                      </div>
                    )}
                    <div className={`whitespace-pre-wrap leading-relaxed ${m.role === 'assistant' ? 'text-zinc-300' : ''}`}>{m.content}</div>
                  </div>
                </div>
              ))}
              {isLoading && (
                <div className="text-xs text-red-500 animate-pulse">Thinking…</div>
              )}
              <div ref={endRef} />
            </div>
          </ScrollArea>

          <form onSubmit={send} className="mt-3 flex gap-2">
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Describe your laser problem... (e.g., 'My cuts aren't going all the way through')"
              className="bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
              disabled={isLoading || !token}
              data-testid="laser-ai-input"
            />
            <Button
              type="submit"
              disabled={isLoading || !input.trim() || !token}
              className="bg-red-600 hover:bg-red-500"
              data-testid="laser-ai-send"
            >
              <Send className="w-4 h-4" />
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default LaserAskAI;
